import React from 'react';
import {
  VStack, HStack, Text, Icon,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import {
  FileText, MessageSquare, Target, CheckSquare, LucideIcon,
} from 'lucide-react-native';

import { Task } from '@/student/types';
import { taskRequiresEvaluation } from '../../../common/utils/taskEvaluationUtils';
import { useTenantEventKinds } from '../../../common/context/TenantSettingsStore';
import { isOralExamTask } from '../../../common/utils/tasks/isOralExamTask';
import { wordCountBounds } from '../../../common/utils/wordCount';

interface RequirementRowProps {
  icon: LucideIcon;
  label: string;
  value: string;
  variant?: 'compact' | 'comfortable';
}

const RequirementRow: React.FC<RequirementRowProps> = ({
  icon, label, value, variant = 'comfortable',
}) => {
  const isCompact = variant === 'compact';
  return (
    <HStack space="md" alignItems="flex-start">
      <Icon size="2xs" as={icon} color="$textSubtle" mt="$1" />
      <VStack space="xs" flex={1}>
        <Text
          size="2xs"
          color="$textHint"
          fontWeight="bold"
          textTransform="uppercase"
          letterSpacing="$md"
        >
          {label}
        </Text>
        <Text
          size={isCompact ? 'xs' : 'sm'}
          color="$textHeading"
          lineHeight="$md"
        >
          {value}
        </Text>
      </VStack>
    </HStack>
  );
};

interface TaskRequirementsProps {
  task: Task;
  tasks?: Task[];
  hideCompletionPolicy?: boolean;
  hideBorder?: boolean;
  variant?: 'compact' | 'comfortable';
}

/**
 * TaskRequirements
 * ----------------
 * Displays task metadata and requirements:
 * - Evaluation mode (self-verification, AI-evaluated, etc.)
 * - Word count targets (for essays)
 * - Citation requirements
 * - Completion policy
 *
 * This is a functional skeleton - minimal UI, complete info.
 */
export const TaskRequirements: React.FC<TaskRequirementsProps> = ({
  task,
  tasks = [],
  hideCompletionPolicy = false,
  hideBorder = false,
  variant = 'comfortable',
}) => {
  const { t } = useTranslation();
  const { eventKinds } = useTenantEventKinds();

  if (!task) return null;

  const isCompact = variant === 'compact';

  // Determine if module has oral exam (affects evaluation message)
  const hasOralExamTask = tasks.some((t) => t.type === 'oral_exam');

  // Task evaluation info
  const evaluationRequired = taskRequiresEvaluation(task, eventKinds);
  const formalReqs = task?.evaluation?.formalCriteria;
  const isEssay = task.type === 'essay';
  const isEvent = task.type === 'event' || isOralExamTask(task);
  const isActivity = task.type === 'activity';
  const isMCQ = task.type === 'multiple_choice';

  const wordCountRange = wordCountBounds(formalReqs);
  const wordcountRequirement = wordCountRange
    ? t('student.module.task-sidebar.wordCountTargetValue', {
      count: wordCountRange.target,
      tolerance: ((formalReqs?.wordcountTolerance ?? 0) * 100).toFixed(0),
    })
    : null;

  // Citations requirement
  const citationsRequired = (formalReqs?.minInTextCitations || 0) > 0;

  // Evaluation mode text
  const getEvaluationText = () => {
    if (!evaluationRequired) {
      return t('student.module.task-sidebar.not-evaluated', 'Not subject to evaluation');
    }
    if (isMCQ) {
      const passThresholdPercent = task.mcqConfig?.passThresholdPercent ?? 70;
      const poolSize = task.mcqConfig?.questions?.length ?? 0;
      const questionsToShow = task.mcqConfig?.questionsToShow ?? null;
      const questionsShown = questionsToShow === null ? poolSize : Math.min(questionsToShow, poolSize);

      if (questionsShown <= 0) {
        return t('student.module.task-sidebar.mcqPassRequirementPercentOnly', { percent: passThresholdPercent });
      }

      const requiredCorrect = Math.ceil((passThresholdPercent / 100) * questionsShown);
      return t('student.module.task-sidebar.mcqPassRequirement', {
        requiredCorrect,
        total: questionsShown,
        percent: passThresholdPercent,
      });
    }
    if (isActivity) {
      return t('student.module.task-sidebar.activityMessage', 'Activity-based completion');
    }
    if (isOralExamTask(task) && task.submission?.state === 'marked_as_done') {
      return t('student.module.task-sidebar.oralExamCompletedMessage', 'This exam is complete.');
    }
    if (isEvent) {
      return t('student.module.task-sidebar.eventMessage', 'Attendance-based completion');
    }
    if (hasOralExamTask) {
      return t('student.module.task-sidebar.oral-exam-module-message', 'Module includes oral exam');
    }

    const gradingType = task?.evaluation?.mode === 'auto' ? 'plt' : task?.gradingSystem;
    return t(`student.module.task-sidebar.grading-system-message.${gradingType}`, gradingType);
  };

  // Completion policy text
  const completionPolicy = task?.evaluation?.completionPolicyEffective;
  const completionPolicyText = isOralExamTask(task)
    ? t(
      'student.module.task-sidebar.oralExamCompletionPolicy',
      'The exam marks this task done when the sitting ends.',
    )
    : t(
      `student.module.task-sidebar.completionPolicies.${completionPolicy}`,
      completionPolicy,
    );

  return (
    <VStack space="lg">
      {/* Section Title - Only show if not hidden by border flag (external control) */}
      {!hideBorder && (
        <Text
          size={isCompact ? '2xs' : 'xs'}
          color="$textHint"
          fontWeight="bold"
          textTransform="uppercase"
          letterSpacing="$md"
        >
          {t('student.module.task-sidebar.title', 'Formal Requirements')}
        </Text>
      )}

      {/* Requirements List */}
      <VStack space="lg">
        {/* Evaluation Mode */}
        <RequirementRow
          icon={Target}
          label={t('student.module.task-sidebar.evaluation', 'Evaluation')}
          value={getEvaluationText() as string}
          variant={variant}
        />

        {/* Word Count (essays only) */}
        {!!(isEssay && wordcountRequirement) && (
          <RequirementRow
            icon={FileText}
            label={t('student.module.task-sidebar.target-wordcount', 'Target Wordcount')}
            value={wordcountRequirement as string}
            variant={variant}
          />
        )}

        {/* Citations (essays only) */}
        {!!isEssay && (
          <RequirementRow
            icon={MessageSquare}
            label={t('student.module.task-sidebar.citations-requirement', 'Citations Requirement')}
            value={
              (citationsRequired
                ? t('student.module.task-sidebar.citations-required', 'Citations and reference list required')
                : t('student.module.task-sidebar.citations-not-required', 'Not required')) as string
            }
            variant={variant}
          />
        )}

        {/* Completion Policy */}
        {!hideCompletionPolicy && (
          <RequirementRow
            icon={CheckSquare}
            label={t('student.module.task-sidebar.completionPolicy', 'Completion Policy')}
            value={completionPolicyText as string}
            variant={variant}
          />
        )}
      </VStack>
    </VStack>
  );
};

export default TaskRequirements;
